import { useEffect, useMemo, useRef } from "react";
import { Button } from "../../../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { Save } from "lucide-react";

import { useTagesverlauf } from "../hooks";
import type { TimelineLesson, TagesverlaufProps } from "./shared-hooks";
import { ExcursionBanner, BulkActionBar } from "./TagesverlaufControls";
import { LessonRow } from "./TagesverlaufLessonRow";
import { useGetScheduleOverridesQuery } from "../../../attendance/api/attendanceApi";

// Re-export TimelineLesson for consumers
export type { TimelineLesson };

/**
 * Check if a lesson time falls outside the Kurzbeschulung window.
 * Returns true if the lesson should be hidden (outside window + absent_excused).
 */
export function isOutsideKBWindow(
  lessonStart: string,
  lessonEnd: string,
  kbStart: string,
  kbEnd: string,
): boolean {
  // Compare HH:MM strings directly (lexicographic works for time)
  const ls = lessonStart.slice(0, 5);
  const le = lessonEnd.slice(0, 5);
  const ks = kbStart.slice(0, 5);
  const ke = kbEnd.slice(0, 5);
  // Lesson is outside if it ends before KB start or starts at/after KB end
  return le <= ks || ls >= ke;
}

export default function Tagesverlauf({
  lessons,
  studentId,
  onUpdate,
  dateIso,
  defaultOpen = false,
  onOpenDiary,
  showDeleteButton = false,
  onDeleteNote,
  autoBulkStatus,
  statusLabelOverrides,
  hideSaveControls = false,
  onPendingChangesChange,
  resolutionLabelByLessonId,
}: TagesverlaufProps) {
  const {
    // State
    isOpen,
    setIsOpen,
    changes,
    latenessDetails,
    setLatenessDetails,
    saving,
    deleting,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleteError,
    openLatenessPopover,
    setOpenLatenessPopover,
    showDeleteAttendanceConfirm,
    setShowDeleteAttendanceConfirm,
    setDeletingAttendanceLessonId,
    tripAttendanceStatus,
    savingTripAttendance,
    hasChanges,
    isSuperAdmin,
    isDebugMode,
    // Handlers
    setLessonStatus,
    setLatenessInfo,
    setAllStatus,
    saveChanges,
    handleDeleteNote,
    handleDeleteCancelledLessonAttendance,
    handleTripAttendanceChange,
    handleDeleteExcursionLessonAttendance,
    getEffectiveStatus,
    isSecondClickReady,
  } = useTagesverlauf({
    lessons,
    studentId,
    dateIso,
    onUpdate,
    onDeleteNote,
  });

  // Set initial open state
  if (defaultOpen && !isOpen) {
    setIsOpen(true);
  }

  // Auto-apply the proposed klärung status to every past lesson once
  // the timeline has loaded. Used by the klärung popup so picking
  // "Informiert" flips the whole day (including lessons that already
  // had a status like Ungeklärt) to match the teacher's intent —
  // forceOverwrite skips the "only empty cells" first-click rule.
  // Guarded so it only runs once per (autoBulkStatus, lessons
  // identity) pair — manual edits afterwards are preserved.
  const autoBulkAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoBulkStatus) return;
    if (lessons.length === 0) return;
    const key = `${autoBulkStatus}:${lessons.length}`;
    if (autoBulkAppliedRef.current === key) return;
    autoBulkAppliedRef.current = key;
    setAllStatus(autoBulkStatus, { forceOverwrite: true });
  }, [autoBulkStatus, lessons, setAllStatus]);

  // Bubble pending changes to the parent on every change — used by
  // dialogs that own a single commit gate (e.g. AbsenceConfirmDialog
  // hides the timeline's own save buttons and persists the changes
  // alongside its SAN/Prüfung on its own Speichern click).
  useEffect(() => {
    onPendingChangesChange?.(changes);
  }, [changes, onPendingChangesChange]);

  // Fetch Kurzbeschulung override for this student
  const { data: overrides } = useGetScheduleOverridesQuery(
    { studentIds: [studentId] },
    { skip: !studentId },
  );
  // Only use KB override if the selected date falls within its validity range
  const kbOverride = useMemo(() => {
    if (!overrides?.length || !dateIso) return undefined;
    const d = dateIso.slice(0, 10);
    return overrides.find((o) => {
      if (o.override_type !== "kurzbeschulung") return false;
      if (o.valid_from && d < o.valid_from.slice(0, 10)) return false;
      if (o.valid_until && d > o.valid_until.slice(0, 10)) return false;
      return true;
    });
  }, [overrides, dateIso]);

  // Filter lessons: hide out-of-window lessons with absent_excused/no status
  const filteredLessons = useMemo(() => {
    if (!kbOverride) return lessons;
    return lessons.filter((lesson) => {
      // Class trips are never KB-filtered
      if (lesson.classAbsenceId) return true;
      const st = String(lesson.status || "").toLowerCase();
      // If lesson has real attendance (present, late, unexcused, unclear), always show
      if (
        st === "present" ||
        st.startsWith("late") ||
        st === "absent_unexcused" ||
        st === "absent_unclear"
      ) {
        return true;
      }
      // If lesson is outside KB window and has absent_excused or no status, hide it
      if (
        isOutsideKBWindow(
          lesson.start,
          lesson.end,
          kbOverride.expected_start_time,
          kbOverride.expected_end_time,
        )
      ) {
        return false;
      }
      return true;
    });
  }, [lessons, kbOverride]);

  return (
    <div className="rounded-md border max-w-[700px]">
      <button
        type="button"
        className="w-full text-left px-4 py-2 flex items-center justify-between"
        onClick={() => setIsOpen((v) => !v)}
      >
        <div className="font-medium">
          Tagesverlauf ({filteredLessons.length})
        </div>
        <span className="text-xs text-muted-foreground">
          {isOpen ? "–" : "+"}
        </span>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 space-y-3">
          {/* Class Excursion Banner */}
          {filteredLessons[0]?.classAbsenceId && (
            <ExcursionBanner
              lesson={filteredLessons[0]}
              tripAttendanceStatus={tripAttendanceStatus}
              savingTripAttendance={savingTripAttendance}
              onTripAttendanceChange={handleTripAttendanceChange}
            />
          )}

          {/* Bulk actions */}
          <BulkActionBar
            lessons={filteredLessons}
            hasChanges={hasChanges}
            saving={saving}
            deleting={deleting}
            showDeleteButton={showDeleteButton}
            isSuperAdmin={isSuperAdmin}
            dateIso={dateIso}
            isSecondClickReady={isSecondClickReady}
            setAllStatus={setAllStatus}
            setShowDeleteConfirm={setShowDeleteConfirm}
            saveChanges={saveChanges}
            hideSaveButton={hideSaveControls}
            statusLabelOverrides={statusLabelOverrides}
          />

          {/* Lessons */}
          <ul className="pl-1 space-y-2">
            {filteredLessons.map((lesson, idx) => {
              // Attendance can't be marked on lessons whose start time
              // is still in the future — teachers shouldn't pre-stamp
              // a kid as absent for a class that hasn't begun.
              // lesson.start is a time-only string ("09:50:00"); we
              // compose it with the day's dateIso (YYYY-MM-DD) to get
              // a real timestamp for comparison.
              const lessonDateIso = dateIso ? dateIso.slice(0, 10) : "";
              const lessonStartTs = lessonDateIso && lesson.start
                ? new Date(`${lessonDateIso}T${lesson.start}`).getTime()
                : NaN;
              const isFutureLesson = Number.isFinite(lessonStartTs)
                && lessonStartTs > Date.now();
              return (
                <LessonRow
                  key={idx}
                  idx={idx}
                  lesson={lesson}
                  lessons={filteredLessons}
                  changes={changes}
                  latenessDetails={latenessDetails}
                  setLatenessDetails={setLatenessDetails}
                  openLatenessPopover={openLatenessPopover}
                  setOpenLatenessPopover={setOpenLatenessPopover}
                  tripAttendanceStatus={tripAttendanceStatus}
                  savingTripAttendance={savingTripAttendance}
                  isDebugMode={isDebugMode}
                  getEffectiveStatus={getEffectiveStatus}
                  setLessonStatus={setLessonStatus}
                  setLatenessInfo={setLatenessInfo}
                  handleTripAttendanceChange={handleTripAttendanceChange}
                  handleDeleteExcursionLessonAttendance={
                    handleDeleteExcursionLessonAttendance
                  }
                  setDeletingAttendanceLessonId={setDeletingAttendanceLessonId}
                  setShowDeleteAttendanceConfirm={setShowDeleteAttendanceConfirm}
                  onOpenDiary={onOpenDiary}
                  isFutureDate={isFutureLesson}
                  statusLabelOverrides={statusLabelOverrides}
                  resolutionLabel={
                    lesson.lesson_id
                      ? resolutionLabelByLessonId?.[lesson.lesson_id]
                      : undefined
                  }
                />
              );
            })}
          </ul>

          {hasChanges && !hideSaveControls && (
            <div className="pt-2 flex justify-end">
              <Button size="sm" onClick={saveChanges} disabled={saving}>
                <Save className="h-3 w-3 mr-1" />
                {saving
                  ? "Speichern..."
                  : `Änderungen speichern (${Object.keys(changes).length})`}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abwesenheitseintrag löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchtest du diesen Abwesenheitseintrag wirklich löschen?
              <br />
              <br />
              <strong>Hinweis:</strong> Nur der Abwesenheitseintrag
              (Krankmeldung/Entschuldigung) wird gelöscht. Die
              Anwesenheitseinträge der einzelnen Stunden bleiben erhalten.
              {deleteError && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                  {deleteError}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? "Löschen..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete erroneous attendance dialog */}
      <AlertDialog
        open={showDeleteAttendanceConfirm}
        onOpenChange={setShowDeleteAttendanceConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anwesenheitsdaten löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Stunde ist entfallen. Möchtest du die fälschlicherweise
              erfassten Anwesenheitsdaten löschen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowDeleteAttendanceConfirm(false);
                setDeletingAttendanceLessonId(null);
              }}
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCancelledLessonAttendance}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
